import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { BoldIcon, ItalicIcon } from "lucide-react";
import { ListBulletsIcon, ListNumbersIcon } from "@phosphor-icons/react";

type TSlideOutlineEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isLabs: boolean;
  isReadOnly?: boolean;
  isSelected?: boolean;
};

export function SlideOutlineEditor(props: TSlideOutlineEditorProps) {
  const { t } = useTranslation("projectHome");
  const {
    value,
    onChange,
    placeholder = t("presentation.keyPointsPlaceholder"),
    isLabs,
    isReadOnly = false,
    isSelected = true
  } = props;

  const effectiveReadOnly = isReadOnly || !isSelected;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        hardBreak: false
      }),
      Placeholder.configure({ placeholder })
    ],
    content: value,
    editable: !effectiveReadOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          "text-innoveraZinc-900 w-full h-full resize-none border-none outline-none px-2 py-1 bg-transparent focus:outline-none max-w-none",
          isLabs ? "text-[14px] prose prose-sm" : "text-lg prose prose-base"
        )
      }
    }
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value);
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!effectiveReadOnly, false);
  }, [editor, effectiveReadOnly]);

  const ToolbarButton = useCallback(
    ({
      isActive,
      onClick,
      children,
      title,
      disabled
    }: {
      isActive: boolean;
      onClick: () => void;
      children: React.ReactNode;
      title: string;
      disabled?: boolean;
    }) => (
      <button
        type="button"
        onClick={onClick}
        title={title}
        disabled={disabled}
        className={cn(
          "p-2 rounded-lg text-[14px] font-medium transition-colors border border-transparent",
          disabled
            ? "cursor-default"
            : isActive
            ? "bg-white border-innoveraZinc-150 text-innoveraZinc-700"
            : "text-innoveraZinc-500 hover:bg-innoveraZinc-100 hover:text-innoveraZinc-700"
        )}
      >
        {children}
      </button>
    ),
    []
  );

  if (!editor) return null;

  return (
    <div className="flex flex-col h-full">
      {!isReadOnly && (
        <div className="flex items-center gap-1 p-1 bg-innoveraZinc-50 border-b border-innoveraZinc-150">
          <ToolbarButton
            isActive={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title={t("presentation.bold")}
            disabled={!isSelected}
          >
            <BoldIcon className="w-4 h-4" />
          </ToolbarButton>

          <ToolbarButton
            isActive={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title={t("presentation.italic")}
            disabled={!isSelected}
          >
            <ItalicIcon className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-4 bg-innoveraZinc-200 mx-1" />

          <ToolbarButton
            isActive={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title={t("presentation.bulletList")}
            disabled={!isSelected}
          >
            <ListBulletsIcon className="w-4 h-4" />
          </ToolbarButton>

          <ToolbarButton
            isActive={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title={t("presentation.numberedList")}
            disabled={!isSelected}
          >
            <ListNumbersIcon className="w-4 h-4" />
          </ToolbarButton>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4">
        <EditorContent
          editor={editor}
          className={cn(
            "h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:focus:outline-none [&_.ProseMirror_p]:leading-[22px] [&_.ProseMirror_li]:leading-[22px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-innoveraZinc-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5",
            "[&_.ProseMirror]:text-[14px] [&_.ProseMirror_p]:text-[14px] [&_.ProseMirror_strong]:text-[14px] [&_.ProseMirror_li]:text-[14px]"
          )}
        />
      </div>
    </div>
  );
}
